import { Button, ButtonSize } from '@atlas/components';
import cx from 'classnames';
import {
  type ChangeEvent,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { createWithdrawalRequest } from 'api/payments';
import Input from 'components/Form/fields/Input';
import Select from 'components/Form/fields/Select';
import Icon from 'components/Icon/Icon';
import { useClientProfile } from 'store';
import { Translation, useTranslation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useIntl from 'utils/useIntl';
import useLazyRequest from 'utils/useLazyRequest';
import { validatePixKey } from 'utils/validatePixKey';

import PixWithdrawalResult from './PixWithdrawalResult';

import type { PaymentPopupDataType } from 'config/types';
import styles from './PixForm.module.scss';

export type PixWithdrawalFormProps = {
  data: PaymentPopupDataType;
  onNavigateToTransactionHistory?: () => void;
};

export const PIX_TYPES = {
  CPF: 'cpf',
  EMAIL: 'email',
  EVP: 'evp',
  PHONE: 'phone',
};

type ErrorType =
  | {
      message: string;
      params: { ruleOption: number };
    }
  | {
      message: string;
    }
  | null
  | undefined;

const optionsPixtype = Object.values(PIX_TYPES).map((item) => ({
  text: `withdrawal_pixstarspay_pixtype_${item}`,
  value: item,
}));

const PixWithdrawalForm: FC<PixWithdrawalFormProps> = ({
  data,
  onNavigateToTransactionHistory,
}) => {
  const { redirectUrl, pmId, paymentName, imageSources } = data;
  const [pixType, setPixType] = useState(optionsPixtype[0].value);
  const [error, setError] = useState<ErrorType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    loading,
    execute,
    error: withdrawalError,
    data: withdrawalData,
  } = useLazyRequest(createWithdrawalRequest);
  const [{ sessionToken }] = useClientProfile();
  const { formatMoney } = useIntl();
  const pixSelectLabel = useTranslation('type_of_confirmation');

  const parsedData = useMemo<Record<PropertyKey, string | undefined>>(() => {
    const url = new URL(redirectUrl, window.location.origin);
    const searchParams = new URLSearchParams(url.search);

    return Array.from(searchParams.entries()).reduce<
      Record<PropertyKey, string | undefined>
    >((acc, [key, value]) => {
      let processedValue: string | undefined = value;
      if (value === 'null' || value === 'undefined') {
        processedValue = undefined;
      }

      acc[key] = processedValue;
      return acc;
    }, {});
  }, [redirectUrl]);
  const [pixKey, setPixKey] = useState(parsedData[pixType]);

  const handleChangePixType = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      setPixType(value);

      let newPixKey: string = parsedData[value] || '';

      if (value === PIX_TYPES.PHONE) {
        newPixKey = `+${newPixKey.toString().trim()}`;
      }

      setError(null);
      setPixKey(newPixKey);
    },
    [parsedData],
  );

  const handleChangePixKey = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value.trim();

      if (pixType === PIX_TYPES.PHONE) {
        value = value ? `+${value.replace(/\D/g, '')}` : '';
      }

      const error = validatePixKey(pixType, value);

      setError(error);
      setPixKey(value);
    },
    [pixType],
  );

  const handleClickButton = useCallback(() => {
    if (isSubmitting) return;

    const { amount, currency, paymentId } = parsedData;

    const requestData = {
      amount: Number(amount),
      currency,
      additionalParameters: {
        pixKey,
        pixKeyType: pixType,
        publicPaymentId: paymentId,
      },
      pmId: pmId.toString(),
    };

    setIsSubmitting(true);

    execute({
      body: requestData,
      headers: {
        authorization: `Bearer ${sessionToken}`,
      },
    });
  }, [execute, pixKey, pmId, pixType, sessionToken, isSubmitting, parsedData]);

  useEffect(() => {
    return () => {
      setIsSubmitting(false);
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      {loading === LoadingState.success && (
        <PixWithdrawalResult
          type="success"
          image={imageSources.WITHDRAWAL}
          paymentName={paymentName}
          onNavigateToTransactionHistory={onNavigateToTransactionHistory}
        />
      )}

      {loading === LoadingState.failed && (
        <PixWithdrawalResult
          type="error"
          image={imageSources.WITHDRAWAL}
          paymentName={paymentName}
          onNavigateToTransactionHistory={onNavigateToTransactionHistory}
        />
      )}

      {!withdrawalError && !withdrawalData && (
        <div className={styles.withdrawal}>
          <div className={styles.payment}>
            <div className={styles['payment-title']}>
              <span className={styles['payment-text']}>
                <Translation
                  message="withdrawal_with"
                  params={{ name: paymentName }}
                />
              </span>
              {imageSources.WITHDRAWAL && (
                <img
                  className={styles['payment-image']}
                  src={imageSources.WITHDRAWAL}
                  alt={paymentName}
                />
              )}
            </div>
            <div className={styles['payment-amount']}>
              <Translation message="amount" />
              :&nbsp;
              <span className={styles.bold}>
                {formatMoney(Number(parsedData.amount))}
              </span>
            </div>
          </div>
          <p className={styles.text}>
            <Translation message="please_confirm_your_transaction" />
          </p>
          <div className={styles.form}>
            <Select
              className={styles.select}
              onChange={handleChangePixType}
              options={optionsPixtype}
              value={pixType}
              isValid={true}
              placeholder={pixSelectLabel}
              translateOptions={true}
            />

            <div className={styles['input-field']}>
              <Input
                disabled={pixType === PIX_TYPES.CPF}
                isValid={!error}
                onChange={handleChangePixKey}
                value={pixKey}
                required
              />

              {error && (
                <div className={cx(styles.error, styles['error-text'])}>
                  <Translation
                    message={error.message}
                    {...('params' in error ? { params: error.params } : {})}
                  />
                </div>
              )}
            </div>

            <Button
              wide
              size={ButtonSize.big}
              onClick={handleClickButton}
              icon={<Icon name="wallet-minus.bold" />}
              data-testid="submit-pix-withdrawal"
              disabled={!pixKey || !!error || isSubmitting}
              className={styles.button}
            >
              <Translation message="withdrawal" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PixWithdrawalForm;
